import { useState, useMemo } from 'react'
import { Option } from 'react-select'
import { useScope } from 'src/services/i18n/LocalizationProvider'
import * as Uuid from 'src/services/constants/uuid.yml'
import { usePermissionCheck } from 'src/services/permissions/components/PermissionCheck'
import {
  IPeopleDropdownOption,
  IPeopleDropdownValue,
  IPersonOption,
  isBusinessUnitValue,
  isPeopleJobsValue,
} from './types'
import { IPeopleDropdownSearchResult, usePerformPeopleDropdownSearch } from './gql'

const minSearchLength = 2

interface IUsePeopleSearchExtraDetails {
  showIndividualJobs: boolean
  currentlySelected: Array<Option<IPeopleDropdownValue>>
  excludedValues?: IPeopleDropdownValue[]
  hierarchicalPermissionsFilter?: string[]
  showNoJobRecords: boolean
  selectableValues: 'people_jobs_only' | 'people_jobs_business_units'
  validJobIds?: string[]
  hasUserAccount?: boolean
}

const matchesResult = (
  value: IPeopleDropdownValue,
  result: IPeopleDropdownSearchResult,
  showIndividualJobs: boolean,
): boolean => {
  if (isBusinessUnitValue(value) && result.businessUnit) {
    return value.businessUnitId === result.businessUnit.id
  }

  if (isPeopleJobsValue(value)) {
    if (showIndividualJobs) {
      const selectedPerson = value.personId === result.person.id
      const selectedJob = (value.jobId ?? undefined) === result.job?.id
      return selectedPerson && selectedJob
    }

    return value.personId === result.person.id
  }

  return false
}

const usePeopleSearch = (
  searchText: string,
  {
    showIndividualJobs,
    currentlySelected,
    excludedValues,
    hierarchicalPermissionsFilter,
    selectableValues,
    showNoJobRecords,
    validJobIds,
    hasUserAccount,
  }: IUsePeopleSearchExtraDetails,
) => {
  const [showPastStaff, setShowPastStaff] = useState(false)
  const t = useScope('edm:features.PeopleDropdownInput')
  const { data: dropdownSearchResults, loading } = usePerformPeopleDropdownSearch(
    {
      searchText,
      showPastStaff,
      showIndividualJobs,
      hierarchicalPermissionsFilter,
      showNoJobRecords,
      validJobIds,
      hasUserAccount,
    },
    searchText.length < minSearchLength,
  )

  const { passedPermissionSlugs } = usePermissionCheck({
    targetType: 'functional',
    permissionSlugs: ['people.past_profile.view'],
  })
  const hasPastPeoplePermission = passedPermissionSlugs?.includes('people.past_profile.view')

  const results = useMemo(
    () =>
      dropdownSearchResults?.performPeopleDropdownSearch.results.filter((result) => {
        const isSelected = currentlySelected.some(
          (current) => current.value !== undefined && matchesResult(current.value, result, showIndividualJobs),
        )

        if (isSelected) {
          return false
        }

        const isExcluded = excludedValues?.some((value) => matchesResult(value, result, showIndividualJobs)) ?? false

        return !isExcluded
      }) ?? [],
    [currentlySelected, dropdownSearchResults, excludedValues, showIndividualJobs],
  )

  const peopleSearchOptions = useMemo(() => {
    const options: IPeopleDropdownOption[] = []

    if (hasPastPeoplePermission) {
      options.push({
        type: 'control',
        toggleSet: () => setShowPastStaff(!showPastStaff),
        isSet: showPastStaff,
        name: t('optionControlShowPastStaff'),
      })
    }

    if (searchText.length < minSearchLength) {
      options.push({
        type: 'info',
        disabled: true,
        message: t('optionInfoSearchTooShort', { minSearchLength }),
      })

      return options
    }

    if (dropdownSearchResults?.performPeopleDropdownSearch.noResultsReason === 'NO_RESULTS' && !loading) {
      options.push({
        type: 'info',
        disabled: true,
        message: t('optionInfoNoResults'),
      })

      return options
    }

    if (dropdownSearchResults?.performPeopleDropdownSearch.noResultsReason === 'NO_PERMISSION' && !loading) {
      options.push({
        type: 'info',
        disabled: true,
        message: t('optionInfoNoPermission'),
      })

      return options
    }

    if (results) {
      options.push(
        ...results.flatMap<IPeopleDropdownOption>((data, index, businessUnitGroups) => {
          const businessUnitName = data.businessUnit?.name ?? t('optionBusinessUnitNone')

          const person: IPersonOption = {
            type: 'person',
            person: data.person,
            job: data.job,
            otherJobs: !showIndividualJobs ? data.otherJobs : [],
            value: {
              person: data.person,
              job: data.job ?? null,
              personId: data.person.id,
              jobId: data.job?.id ?? null,
            },
          } as const

          if (businessUnitGroups[index - 1]?.businessUnit?.id !== data.businessUnit?.id) {
            return [
              {
                type: 'businessUnit',
                disabled: selectableValues !== 'people_jobs_business_units' || !data.businessUnit?.id,
                value: {
                  businessUnitId: data.businessUnit?.id ?? Uuid.NIL,
                },
                name: businessUnitName,
              },
              person,
            ]
          }

          return [person]
        }),
      )
    }

    return options
  }, [
    showPastStaff,
    t,
    searchText.length,
    dropdownSearchResults,
    loading,
    results,
    showIndividualJobs,
    selectableValues,
    hasPastPeoplePermission,
  ])

  return {
    options: peopleSearchOptions,
    loading,
  }
}

export { usePeopleSearch }
